import React from 'react'
import { createContext } from 'lib/createContext'
import { Loading, Message } from '@workwhile/ui'
import { parseErrorMessage } from 'api'
import { usePositionsQuery } from 'queries/positions/usePositionsQuery'
import { useCompaniesQuery } from 'queries/companies'
import { useCompanyGroupsQuery } from 'queries/companyGroups'
import { usePaginationQueryState } from 'lib/nuqs/usePaginationQueryState'
import { usePositionQueryState } from '../hooks/usePositionQueryState'
import { useCompanyQueryState } from '../hooks/useCompanyQueryState'
import { useSelectedColumnsQueryState } from '../hooks/useSelectedColumnsQueryState'
import { SelectableColumns } from '../components/PositionsTable/ColumnsFilter'

type PositionsContextState = {
  state: {
    data: NonNullable<ReturnType<typeof usePositionsQuery>['data']>
    dataUpdatedAt: number
    isFetching: boolean
    isError: boolean
    error: string
    companies: NonNullable<ReturnType<typeof useCompaniesQuery>['data']>
    companyGroups: NonNullable<ReturnType<typeof useCompanyGroupsQuery>['data']>
    pagination: {
      pageIndex: number
      pageSize: number
    }
    positionFilters: {
      positionIds: number[]
      status: 'approved' | 'unapproved' | 'all'
      prescreenStatus: 'prescreened' | 'not-prescreened' | 'all'
    }
    companyFilters: {
      companyIds: number[]
      companyGroupIds: number[]
    }
    selectedColumnsFilters: SelectableColumns[]
  }
  actions: {
    refetch: () => void
    setPagination: ReturnType<typeof usePaginationQueryState>[1]
    setPositionFilters: ReturnType<typeof usePositionQueryState>[1]
    setCompanyFilters: ReturnType<typeof useCompanyQueryState>[1]
    setSelectedColumnsFilters: ReturnType<
      typeof useSelectedColumnsQueryState
    >[1]
  }
}

const [Context, usePositionsContextValue] =
  createContext<PositionsContextState>({
    name: 'PositionsContext',
  })

export const PositionsProvider = ({
  children,
}: {
  children: React.ReactNode
}) => {
  const [pagination, setPagination] = usePaginationQueryState({
    defaultPageSize: 10,
  })
  const [positionFilters, setPositionFilters] = usePositionQueryState()
  const [companyFilters, setCompanyFilters] = useCompanyQueryState()
  const [selectedColumnsFilters, setSelectedColumnsFilters] =
    useSelectedColumnsQueryState()

  const {
    data,
    isPending: isPositionsLoading,
    isError: isPositionsError,
    error: positionsError,
    dataUpdatedAt,
    refetch: refetchPositions,
    isFetching,
  } = usePositionsQuery({
    page: pagination.pageIndex,
    pageSize: pagination.pageSize,
    positionIds: positionFilters.positionIds,
    status: positionFilters.status,
    prescreenStatus: positionFilters.prescreenStatus,
    companyIds: companyFilters.companyIds,
    companyGroupIds: companyFilters.companyGroupIds,
  })

  const {
    data: companies,
    isLoading: isCompaniesLoading,
    isError: isCompaniesError,
    error: companiesError,
    refetch: refetchCompanies,
  } = useCompaniesQuery()

  const {
    data: companyGroups,
    isLoading: isCompanyGroupsLoading,
    isError: isCompanyGroupsError,
    error: companyGroupsError,
    refetch: refetchCompanyGroups,
  } = useCompanyGroupsQuery()

  const isAnyError =
    isPositionsError || isCompaniesError || isCompanyGroupsError

  const error = positionsError || companiesError || companyGroupsError

  const isLoading =
    isPositionsLoading || isCompaniesLoading || isCompanyGroupsLoading

  const refetch = () => {
    refetchPositions()
    refetchCompanies()
    refetchCompanyGroups()
  }

  if (isLoading) {
    return <Loading />
  }

  if (isPositionsError && !data) {
    console.error(positionsError)
    return (
      <Message variant="error" m={4} borderRadius="small">
        Sorry, something went wrong. Please reach out to eng support with the
        URL of the page if this persists.
      </Message>
    )
  }

  if (!data) {
    return null
  }

  const value = {
    state: {
      data,
      dataUpdatedAt,
      isFetching,
      isError: isAnyError,
      error: parseErrorMessage(error),
      companies: companies ?? [],
      companyGroups: companyGroups ?? [],
      pagination,
      positionFilters,
      companyFilters,
      selectedColumnsFilters,
    },
    actions: {
      refetch,
      setPagination,
      setPositionFilters,
      setCompanyFilters,
      setSelectedColumnsFilters,
    },
  }

  return <Context.Provider value={value}>{children}</Context.Provider>
}

export { usePositionsContextValue }
